import { ref, computed, watch, nextTick } from 'vue';
import type { Ref } from 'vue';
import * as Sentry from '@sentry/vue';
import Hls from 'hls.js';
import type { ErrorData } from 'hls.js';

export interface MediaItem {
  id: number | string;
  start: number;
  duration: number;
  type?: string;
}

export interface PlaybackTransport {
  playbackRate: Ref<number>;
  volume: Ref<number>;
  muted: Ref<boolean>;
}

type PlaybackEmit = (event: 'media-change' | 'refresh-expired', payload?: number | string | null) => void;

export interface PlaybackOptions {
  videoEl: Ref<HTMLVideoElement | null>;
  items: Ref<MediaItem[]>;
  src: Ref<string | null>;
  transport: PlaybackTransport;
  emit: PlaybackEmit;
}

const useVideoElementControls = (
  videoEl: Ref<HTMLVideoElement | null>,
  transport: PlaybackTransport,
  playing: Ref<boolean>,
  canPlay: () => boolean,
) => {
  const applyVolume = () => {
    if (!videoEl.value) return;
    videoEl.value.volume = transport.volume.value;
    videoEl.value.muted = transport.muted.value;
  };

  const setRate = (rate: number) => {
    if (videoEl.value) videoEl.value.playbackRate = rate;
  };

  const playSafely = () => {
    if (!videoEl.value) return;
    videoEl.value.play()
      .then(() => { playing.value = true; })
      .catch(() => { playing.value = false; });
  };

  const togglePlay = () => {
    if (!videoEl.value || !canPlay()) return;
    if (playing.value) {
      videoEl.value.pause();
      playing.value = false;
    } else {
      playSafely();
    }
  };

  return { applyVolume, setRate, playSafely, togglePlay };
};

export const useHlsPlaylistPlayback = ({ videoEl, items, src, transport, emit }: PlaybackOptions) => {
  const playing = ref(false);
  const timelineSeconds = ref(0);
  const durationSeconds = ref(0);
  const loading = ref(false);

  const totalSeconds = computed(
    () => durationSeconds.value || items.value.reduce((sum, v) => sum + (v.duration || 0), 0),
  );

  const activeItemId = computed<number | string | null>(() => {
    if (!items.value.length) return null;
    const idx = items.value.findLastIndex((v) => timelineSeconds.value >= (v.start || 0));
    return (idx === -1 ? items.value[0] : items.value[idx]).id;
  });

  const { applyVolume, setRate, playSafely, togglePlay } = useVideoElementControls(
    videoEl,
    transport,
    playing,
    () => Boolean(src.value),
  );

  let hls: Hls | null = null;
  let refreshAttempted = false;
  let pendingRestore: { seconds: number; wasPlaying: boolean } | null = null;
  let jumpTargetId: number | string | null = null;

  const teardown = () => {
    if (hls) {
      hls.destroy();
      hls = null;
    }
  };

  const handleFatal = () => {
    if (!refreshAttempted) {
      refreshAttempted = true;
      pendingRestore = { seconds: timelineSeconds.value, wasPlaying: playing.value };
      emit('refresh-expired');
      return;
    }
    Sentry.captureMessage(`[MediaPlayer] HLS fatal error (${src.value})`);
    playing.value = false;
  };

  const load = () => {
    teardown();
    const el = videoEl.value;
    if (!el || !src.value) return;
    loading.value = true;

    if (Hls.isSupported()) {
      hls = new Hls({ startFragPrefetch: true });
      hls.loadSource(src.value);
      hls.attachMedia(el);
      hls.on(Hls.Events.ERROR, (_event: string, data: ErrorData) => {
        if (data.fatal) handleFatal();
      });
    } else if (el.canPlayType('application/vnd.apple.mpegurl')) {
      el.src = src.value;
    }
  };

  const onTimeUpdate = () => {
    if (videoEl.value) timelineSeconds.value = videoEl.value.currentTime;
  };

  const onLoadedMetadata = () => {
    if (!videoEl.value) return;
    durationSeconds.value = Number.isFinite(videoEl.value.duration) ? videoEl.value.duration : 0;
    videoEl.value.playbackRate = transport.playbackRate.value;
    applyVolume();
    if (pendingRestore) {
      videoEl.value.currentTime = pendingRestore.seconds;
      if (pendingRestore.wasPlaying) playSafely();
      pendingRestore = null;
    }
  };

  const onEnded = () => { playing.value = false; };

  const onCanPlay = () => {
    loading.value = false;
    refreshAttempted = false;
  };

  const onWaiting = () => { loading.value = true; };

  const seekTo = (timelineSec: number) => {
    jumpTargetId = null;
    if (videoEl.value) videoEl.value.currentTime = timelineSec;
  };

  const jumpToItemId = (itemId: number | string) => {
    const item = items.value.find((v) => v.id === itemId);
    if (!item || !videoEl.value) return;
    jumpTargetId = itemId;
    videoEl.value.currentTime = item.start || 0;
    playSafely();
  };

  watch(activeItemId, (id) => {
    if (jumpTargetId !== null) {
      if (id !== jumpTargetId) return;
      jumpTargetId = null;
    }
    emit('media-change', id ?? null);
  }, { immediate: true });
  watch(src, () => nextTick(load));

  return {
    playing,
    timelineSeconds,
    totalSeconds,
    loading,
    onTimeUpdate,
    onLoadedMetadata,
    onEnded,
    onError: handleFatal,
    onCanPlay,
    onWaiting,
    togglePlay,
    seekTo,
    jumpToItemId,
    setRate,
    applyVolume,
    mount: load,
    unmount: teardown,
  };
};
